import React from 'react';
import { InstagramTarget } from '../../types';
import { translations } from '../../translations';

interface QualityScoreProps {
  targets: InstagramTarget[];
  isDarkMode: boolean;
  language?: string;
}

// Function to calculate target quality score
export const calculateQualityScore = (targets: InstagramTarget[]): number => {
  if (targets.length === 0) return 0;

  // Calculate scores for each criterion
  const targetCountScore = Math.min(targets.length / 50, 1); // 50 targets = 100%

  // Calculate average follower score
  const followerScores = targets.map(target => {
    const followers = parseInt(target.followers.replace(/[^0-9]/g, ''), 10);
    if (followers < 2000) return 0;
    if (followers > 25000) return 0;
    return 1;
  });
  const followerScore = followerScores.reduce((a, b) => a + b, 0) / targets.length;

  // Calculate average post score
  const postScores = targets.map(target => Math.min(target.posts / 50, 1));
  const postScore = postScores.reduce((a, b) => a + b, 0) / targets.length;

  // Calculate final score (0-100)
  const finalScore = Math.round((targetCountScore * 0.4 + followerScore * 0.4 + postScore * 0.2) * 100);
  return Math.min(Math.max(finalScore, 0), 100);
};

// Function to get color based on score
const getScoreColor = (score: number): { bg: string; text: string; ring: string } => {
  if (score >= 80) {
    return {
      bg: 'bg-emerald-500',
      text: 'text-emerald-500',
      ring: 'ring-emerald-500'
    };
  } else if (score >= 60) {
    return {
      bg: 'bg-yellow-500',
      text: 'text-yellow-500',
      ring: 'ring-yellow-500'
    };
  } else if (score >= 40) {
    return {
      bg: 'bg-orange-500',
      text: 'text-orange-500',
      ring: 'ring-orange-500'
    };
  }
  return {
    bg: 'bg-red-500',
    text: 'text-red-500',
    ring: 'ring-red-500'
  };
};

export function QualityScore({ targets, isDarkMode, language = 'FR' }: QualityScoreProps) {
  const t = translations[language as keyof typeof translations];
  const score = calculateQualityScore(targets);
  const colors = getScoreColor(score);
  
  // Calculate circle properties
  const size = 48; // Circle size in pixels
  const strokeWidth = 3; // Width of the progress ring
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (100 - score) * circumference / 100; // Reverse the progress for stroke-dashoffset

  return (
    <div className={`p-6 rounded-lg border mb-6 ${
      isDarkMode 
        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
        : 'bg-white border-[#e5e7eb]'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className={`text-lg font-semibold ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>{t.target_quality_score}</h2>
        
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12">
            {/* Background circle */}
            <svg className="w-12 h-12 -rotate-90 transform">
              <circle
                cx="24"
                cy="24"
                r={radius}
                fill="none"
                stroke={isDarkMode ? '#2b2b2b' : '#f3f4f6'}
                strokeWidth={strokeWidth}
              />
              {/* Progress circle */}
              <circle
                cx="24"
                cy="24"
                r={radius}
                fill="none"
                stroke={score >= 80 ? '#10b981' : score >= 60 ? '#eab308' : score >= 40 ? '#f97316' : '#ef4444'}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={progress}
                strokeLinecap="round"
                className="transition-all duration-500 ease-out"
              />
            </svg>
            {/* Score text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-sm font-semibold ${colors.text}`}>{score}</span>
            </div>
          </div>
        </div>
      </div>

      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {t.target_quality_description}
      </p>
    </div>
  );
}